import { useState, useEffect, useCallback } from 'react';
import { campanaAdminService, HierarchicalCampanasStats, LumisialCampanasGroup } from '../services/campanaAdminService';
import { AdminDetalleCampanaModal } from '../components/admin/AdminDetalleCampanaModal';
import { AdminFiltersPanel } from '../components/admin/AdminFiltersPanel';
import { EnviosPendientesPanel } from '../components/admin/EnviosPendientesPanel';
import { CampanaMasterWizardModal } from '../components/CampanaMasterWizardModal';
import { CampanasMasterPanel } from '../components/admin/CampanasMasterPanel';
import { aporteExternoService, CampanaConAportes } from '../services/aporteExternoService';
import { Campaña } from '../lib/supabase';
import { StatusBadge } from '../components/ui/StatusBadge';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useAdminHierarchyFilters } from '../hooks/useAdminHierarchyFilters';
import { useToggleSet } from '../hooks/useToggleSet';
import { useProvinciasDisponibles } from '../hooks/useProvinciasDisponibles';
import { ProgresoBar, calcularProgreso, formatProgreso } from '../utils/progresoUtils';
import { useAuth } from '../context/AuthContext';
import { getZonalAccess, puedeAdministrarMaestra } from '../utils/permissions';
import { FormErrorAlert } from '../components/ui/FormErrorAlert';
import {
  Megaphone,
  ChevronDown,
  ChevronRight,
  Target,
  TrendingUp,
  Building2,
  BarChart3,
  Plus,
  Hash,
  Coins,
} from 'lucide-react';
import { Select } from '../components/ui/Select';

interface SelectedCampana {
  campanaId: string;
  lumisialId: string;
  lumisialNombre: string;
  lumisialCiudad: string;
  diocesisNombre: string;
  provinciaNombre: string;
}

const ESTADO_OPTIONS = [
  { value: 'planificacion', label: 'Planificación' },
  { value: 'en_curso', label: 'En Curso' },
  { value: 'finalizada', label: 'Finalizada' },
];

const GRID_COLS = '1fr 80px 90px 90px 80px';

export default function AdminCampanasPage() {
  const { usuario } = useAuth();
  const zonalAccess = getZonalAccess(usuario);
  const filters = useAdminHierarchyFilters();
  const { filterProvincia, filterDiocesis, filterLumisial, filterEstado, filterBusqueda, hasActiveFilters, isDiocesano, isNacional } = filters;
  let nivelCreacion: 'global' | 'nacional' | 'diocesano' = 'diocesano';
  if (usuario?.rol.nombre === 'admin_global' || usuario?.rol.nombre === 'user_global') nivelCreacion = 'global';
  else if (usuario?.rol.nivel === 2) nivelCreacion = 'nacional';
  const puedeCrearMaestra = puedeAdministrarMaestra(usuario, { nivel_origen: nivelCreacion,
    diocesis_id: usuario?.diocesis_id ?? undefined, provincia_eclesiastica_id: usuario?.provincia_eclesiastica_id ?? undefined });

  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [errorAportes, setErrorAportes] = useState<string | null>(null);
  const [stats, setStats] = useState<HierarchicalCampanasStats>({ provincias: [] });
  const [selectedCampana, setSelectedCampana] = useState<SelectedCampana | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [revisionMaestras, setRevisionMaestras] = useState(0);

  // Estado para aportes de campaña (solo admin diocesano, nacional y global)
  const [campanasConAportes, setCampanasConAportes] = useState<CampanaConAportes[]>([]);
  const [loadingAportes, setLoadingAportes] = useState(false);
  const { expanded: expandedCampanasMaster, toggle: toggleCampanaMaster } = useToggleSet();

  // Hook para provincias (solo admin_global)
  const { 
    provincias: provinciasDisponibles, 
    provinciaSeleccionada: provinciaSeleccionadaAportes, 
    setProvinciaSeleccionada: setProvinciaSeleccionadaAportes 
  } = useProvinciasDisponibles();

  const { expanded: expandedProvincias, toggle: toggleProvincia } = useToggleSet();
  const { expanded: expandedDiocesis, toggle: toggleDiocesis } = useToggleSet();
  const { expanded: expandedLumisiales, toggle: toggleLumisial } = useToggleSet();

  const consultarAportes = useCallback(() => {
    if (!isDiocesano && !isNacional && !filters.isGlobal) return Promise.resolve([]);
    const params = filters.isGlobal ? { provinciaEclesiasticaId: provinciaSeleccionadaAportes }
      : { diocesisId: zonalAccess.diocesisId || undefined,
        provinciaEclesiasticaId: zonalAccess.provinciaEclesiasticaId || undefined };
    return aporteExternoService.getCampanasConAportes(params);
  }, [isDiocesano, isNacional, filters.isGlobal, provinciaSeleccionadaAportes, zonalAccess.diocesisId, zonalAccess.provinciaEclesiasticaId]);
  const recibirAportes = useCallback((datos: CampanaConAportes[]) => {
    setCampanasConAportes(datos); setErrorAportes(null); setLoadingAportes(false);
  }, []);
  const falloAportes = useCallback(() => {
    setErrorAportes('No pudimos consultar los aportes por campaña.'); setLoadingAportes(false);
  }, []);

  const consultarDatos = useCallback(() => campanaAdminService.getCampanasGrouped({
    provinciaEclesiasticaId: filterProvincia || undefined, diocesisId: filterDiocesis || undefined,
    lumisialId: filterLumisial || undefined,
    estado: (filterEstado as 'planificacion' | 'en_curso' | 'finalizada' | 'archivada') || undefined,
    busqueda: filterBusqueda || undefined,
  }), [filterProvincia, filterDiocesis, filterLumisial, filterEstado, filterBusqueda]);
  const recibirDatos = useCallback((data: HierarchicalCampanasStats) => {
    setStats(data); setErrorCarga(null); setLoading(false);
  }, []);
  const falloDatos = useCallback(() => { setErrorCarga('No pudimos consultar las campañas.'); setLoading(false); }, []);
  const loadData = () => {
    setLoading(true);
    void consultarDatos().then(recibirDatos).catch(falloDatos);
  };
  /* La lectura posterior tiene errores propios, separados de la escritura. */
  useEffect(() => {
    let vigente = true;
    consultarDatos().then(datos => { if (vigente) recibirDatos(datos); }).catch(() => { if (vigente) falloDatos(); });
    return () => { vigente = false; };
  }, [consultarDatos, recibirDatos, falloDatos]);


  useEffect(() => {
    let vigente = true;
    consultarAportes().then(datos => { if (vigente) recibirAportes(datos); }).catch(() => { if (vigente) falloAportes(); });
    return () => { vigente = false; };
  }, [consultarAportes, recibirAportes, falloAportes]);

  const handleSelectCampana = (
    campana: Campaña,
    lumisial: LumisialCampanasGroup,
    diocesisNombre: string,
    provinciaNombre: string
  ) => {
    setSelectedCampana({
      campanaId: campana.id,
      lumisialId: lumisial.lumisialId,
      lumisialNombre: lumisial.lumisialNombre,
      lumisialCiudad: lumisial.lumisialCiudad,
      diocesisNombre,
      provinciaNombre,
    });
  };

  const totalCampanas = stats.provincias.reduce((s, p) => s + p.totalCampanas, 0);
  const totalObjetivo = stats.provincias.reduce((s, p) => s + p.totalObjetivo, 0);
  const totalRecaudado = stats.provincias.reduce((s, p) => s + p.totalRecaudado, 0);
  const progresoGlobal = calcularProgreso(totalRecaudado, totalObjetivo);

  const renderCampanaRow = (
    campana: Campaña,
    lumisial: LumisialCampanasGroup,
    diocesisNombre: string,
    provinciaNombre: string
  ) => {
    const progreso = calcularProgreso(campana.monto_recaudado, campana.objetivo_recaudacion);
    return (
      <div key={`campana-${campana.id}`} className="contents">
        <button
          onClick={() => handleSelectCampana(campana, lumisial, diocesisNombre, provinciaNombre)}
          className="col-span-full grid hover:bg-blue-50 transition-colors text-left group border-t border-slate-100"
          style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
        >
          <div className="flex items-center gap-3 py-3 pl-8 min-w-0">
            <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
              <Megaphone className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800 text-sm truncate group-hover:text-blue-700 transition-colors">
                {campana.nombre}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <StatusBadge estado={campana.estado} tipo="actividad" />
                {campana.fecha_inicio && (
                  <span className="text-xs text-slate-400">{formatDate(campana.fecha_inicio)}</span>
                )}
              </div>
              <div className="mt-1 w-24">
                <ProgresoBar recaudado={campana.monto_recaudado} objetivo={campana.objetivo_recaudacion} />
              </div>
            </div>
          </div>
          <div className="text-center py-3 pr-2" />
          <div className="text-center py-3 pr-2">
            <span className="text-sm text-blue-600 font-medium">{formatCurrency(campana.objetivo_recaudacion)}</span>
          </div>
          <div className="text-center py-3 pr-2">
            <span className="text-sm text-emerald-600 font-medium">{formatCurrency(campana.monto_recaudado)}</span>
          </div>
          <div className="text-center py-3 pr-4">
            <span className="text-sm font-bold text-slate-700">{progreso.toFixed(0)}%</span>
          </div>
        </button>
      </div>
    );
  };

  const renderLumisialRows = (
    lumisial: LumisialCampanasGroup,
    diocesisNombre: string,
    provinciaNombre: string
  ) => {
    const rows = [(
      <div key={`lumisial-${lumisial.lumisialId}`} className="contents">
        <button
          onClick={() => toggleLumisial(lumisial.lumisialId)}
          className="col-span-full grid hover:bg-slate-50 transition-colors border-t border-slate-100 first:border-t-0"
          style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
        >
          <div className="flex items-center gap-2 py-3 pl-4 min-w-0">
            {expandedLumisiales.has(lumisial.lumisialId)
              ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
              : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            }
            <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <div className="text-left min-w-0">
              <p className="font-semibold text-slate-800 text-sm truncate">{lumisial.lumisialNombre}</p>
              {lumisial.lumisialCiudad && (
                <p className="text-xs text-slate-400 truncate">{lumisial.lumisialCiudad}</p>
              )}
            </div>
          </div>
          <div className="text-center py-3 pr-2">
            <span className="font-semibold text-slate-700 text-sm">{lumisial.totalCampanas}</span>
          </div>
          <div className="text-center py-3 pr-2">
            <span className="font-semibold text-blue-700 text-sm">{formatCurrency(lumisial.totalObjetivo)}</span>
          </div>
          <div className="text-center py-3 pr-2">
            <span className="font-semibold text-emerald-700 text-sm">{formatCurrency(lumisial.totalRecaudado)}</span>
          </div>
          <div className="text-center py-3 pr-4">
            <span className="font-semibold text-slate-700 text-sm">
              {formatProgreso(lumisial.totalRecaudado, lumisial.totalObjetivo)}
            </span>
          </div>
        </button>
      </div>
    )];

    if (expandedLumisiales.has(lumisial.lumisialId)) {
      if (lumisial.campanas.length === 0) {
        rows.push(
          <div key={`empty-${lumisial.lumisialId}`} className="contents">
            <div className="col-span-full py-4 text-center text-slate-400 text-sm bg-slate-50/50">
              No hay campañas que coincidan con los filtros
            </div>
          </div>
        );
      } else {
        lumisial.campanas.forEach(campana => {
          rows.push(renderCampanaRow(campana, lumisial, diocesisNombre, provinciaNombre));
        });
      }
    }

    return rows;
  };

  const renderCampanasGrid = (
    provinciaId: string | null,
    diocesisId: string | null
  ) => {
    const provincias = stats.provincias.filter(p => !provinciaId || p.provinciaId === provinciaId);

    return provincias.flatMap(p =>
      p.diocesis
        .filter(d => !diocesisId || d.diocesisId === diocesisId)
        .flatMap(d =>
          d.lumisiales.flatMap(lumisial =>
            renderLumisialRows(lumisial, d.diocesisNombre, p.provinciaNombre)
          )
        )
    );
  };

  const renderDiocesisCardNacional = (d: typeof stats.provincias[0]['diocesis'][0], p: typeof stats.provincias[0]) => (
    <div key={d.diocesisId} className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: GRID_COLS }}>
        <button
          onClick={() => toggleDiocesis(d.diocesisId)}
          className="col-span-full grid bg-slate-50 hover:bg-slate-100 transition-colors"
          style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
        >
          <div className="flex items-center gap-2 py-3 pl-4 min-w-0">
            {expandedDiocesis.has(d.diocesisId)
              ? <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
              : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
            }
            <div className="text-left min-w-0">
              <p className="text-xs text-slate-400">
                {d.diocesisTipo === 'diocesis' ? 'Diócesis' : 'Prefectura'}
              </p>
              <p className="font-bold text-slate-800 truncate">{d.diocesisNombre}</p>
            </div>
          </div>
          <div className="text-center py-3 pr-2">
            <p className="text-xs text-slate-400 flex items-center justify-center gap-0.5 mb-0.5"><Hash className="w-3 h-3" /></p>
            <span className="font-bold text-slate-900">{d.totalCampanas}</span>
          </div>
          <div className="text-center py-3 pr-2">
            <p className="text-xs text-blue-400 flex items-center justify-center gap-0.5 mb-0.5"><Target className="w-3 h-3" /></p>
            <span className="font-bold text-blue-700">{formatCurrency(d.totalObjetivo)}</span>
          </div>
          <div className="text-center py-3 pr-2">
            <p className="text-xs text-emerald-400 flex items-center justify-center gap-0.5 mb-0.5"><Coins className="w-3 h-3" /></p>
            <span className="font-bold text-emerald-700">{formatCurrency(d.totalRecaudado)}</span>
          </div>
          <div className="text-center py-3 pr-4">
            <p className="text-xs text-slate-400 flex items-center justify-center gap-0.5 mb-0.5"><BarChart3 className="w-3 h-3" /></p>
            <span className="font-bold text-slate-700">{formatProgreso(d.totalRecaudado, d.totalObjetivo)}</span>
          </div>
        </button>
        {expandedDiocesis.has(d.diocesisId) && (
          renderCampanasGrid(p.provinciaId, d.diocesisId)
        )}
      </div>
    </div>
  );

  const renderDiocesisRowGlobal = (d: typeof stats.provincias[0]['diocesis'][0], p: typeof stats.provincias[0]) => (
    <div key={d.diocesisId} className="contents">
      <button
        onClick={() => toggleDiocesis(d.diocesisId)}
        className="col-span-full grid hover:bg-slate-50 transition-colors border-t border-slate-200"
        style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
      >
        <div className="flex items-center gap-2 py-2.5 pl-8 min-w-0">
          {expandedDiocesis.has(d.diocesisId)
            ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
          }
          <span className="font-semibold text-slate-700 text-sm truncate">
            {d.diocesisTipo === 'diocesis' ? 'Diócesis' : 'Prefectura'} {d.diocesisNombre}
          </span>
        </div>
        <div className="text-center py-2.5 pr-2">
          <span className="font-semibold text-slate-700 text-sm">{d.totalCampanas}</span>
        </div>
        <div className="text-center py-2.5 pr-2">
          <span className="font-semibold text-blue-700 text-sm">{formatCurrency(d.totalObjetivo)}</span>
        </div>
        <div className="text-center py-2.5 pr-2">
          <span className="font-semibold text-emerald-700 text-sm">{formatCurrency(d.totalRecaudado)}</span>
        </div>
        <div className="text-center py-2.5 pr-4">
          <span className="font-semibold text-slate-700 text-sm">
            {formatProgreso(d.totalRecaudado, d.totalObjetivo)}
          </span>
        </div>
      </button>
      {expandedDiocesis.has(d.diocesisId) && (
        renderCampanasGrid(p.provinciaId, d.diocesisId)
      )}
    </div>
  );

  const renderDesglose = () => {
    if (isDiocesano) {
      return (
        <div className="grid" style={{ gridTemplateColumns: GRID_COLS }}>
          <div className="contents">
            <div className="col-span-full grid border-b border-slate-100 bg-slate-50/50" style={{ gridTemplateColumns: GRID_COLS }}>
              <div className="py-2 pl-4" />
              <div className="py-2 text-center">
                <span className="text-xs text-slate-400 flex items-center justify-center gap-1">
                  <Hash className="w-3 h-3" /> Campañas
                </span>
              </div>
              <div className="py-2 text-center">
                <span className="text-xs text-blue-500 flex items-center justify-center gap-1">
                  <Target className="w-3 h-3" /> Objetivo
                </span>
              </div>
              <div className="py-2 text-center">
                <span className="text-xs text-emerald-500 flex items-center justify-center gap-1">
                  <Coins className="w-3 h-3" /> Recaudado
                </span>
              </div>
              <div className="py-2 pr-4 text-center">
                <span className="text-xs text-slate-500 flex items-center justify-center gap-1">
                  <BarChart3 className="w-3 h-3" /> Progreso
                </span>
              </div>
            </div>
          </div>
          {renderCampanasGrid(null, null)}
        </div>
      );
    }

    if (isNacional) {
      return (
        <div className="space-y-2">
          {stats.provincias.flatMap(p =>
            p.diocesis.map(d => renderDiocesisCardNacional(d, p))
          )}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {stats.provincias.map(p => (
          <div key={p.provinciaId} className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="grid" style={{ gridTemplateColumns: GRID_COLS }}>
              <button
                onClick={() => toggleProvincia(p.provinciaId)}
                className="col-span-full grid bg-slate-50 hover:bg-slate-100 transition-colors"
                style={{ gridTemplateColumns: GRID_COLS, alignItems: 'center' }}
              >
                <div className="flex items-center gap-2 py-3 pl-4 min-w-0">
                  {expandedProvincias.has(p.provinciaId)
                    ? <ChevronDown className="w-5 h-5 text-slate-600 flex-shrink-0" />
                    : <ChevronRight className="w-5 h-5 text-slate-600 flex-shrink-0" />
                  }
                  <span className="font-bold text-slate-800 truncate">{p.provinciaNombre}</span>
                </div>
                <div className="text-center py-3 pr-2">
                  <p className="text-xs text-slate-400 flex items-center justify-center gap-0.5 mb-0.5">
                    <Hash className="w-3 h-3" /><span>Campañas</span>
                  </p>
                  <span className="font-bold text-slate-900">{p.totalCampanas}</span>
                </div>
                <div className="text-center py-3 pr-2">
                  <p className="text-xs text-blue-500 flex items-center justify-center gap-0.5 mb-0.5">
                    <Target className="w-3 h-3" /><span>Objetivo</span>
                  </p>
                  <span className="font-bold text-blue-700">{formatCurrency(p.totalObjetivo)}</span>
                </div>
                <div className="text-center py-3 pr-2">
                  <p className="text-xs text-emerald-500 flex items-center justify-center gap-0.5 mb-0.5">
                    <Coins className="w-3 h-3" /><span>Recaudado</span>
                  </p>
                  <span className="font-bold text-emerald-700">{formatCurrency(p.totalRecaudado)}</span>
                </div>
                <div className="text-center py-3 pr-4">
                  <p className="text-xs text-slate-500 flex items-center justify-center gap-0.5 mb-0.5">
                    <BarChart3 className="w-3 h-3" /><span>Progreso</span>
                  </p>
                  <span className="font-bold text-slate-700">{formatProgreso(p.totalRecaudado, p.totalObjetivo)}</span>
                </div>
              </button>

              {expandedProvincias.has(p.provinciaId) && p.diocesis.map(d =>
                renderDiocesisRowGlobal(d, p)
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1">Campañas</h1>
          <p className="text-slate-500">Vista consolidada de campañas por zona</p>
        </div>
        {puedeCrearMaestra && <button
          onClick={() => { if (puedeCrearMaestra) setShowWizard(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Distribuir Campaña
        </button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <Megaphone className="w-5 h-5 text-slate-500" />
            <p className="text-sm text-slate-500">Total Campañas</p>
          </div>
          <p className="text-3xl font-bold text-slate-900">{totalCampanas}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <Target className="w-5 h-5 text-blue-500" />
            <p className="text-sm text-slate-500">Total Objetivo</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{formatCurrency(totalObjetivo)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            <p className="text-sm text-slate-500">Total Recaudado</p>
          </div>
          <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalRecaudado)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <BarChart3 className="w-5 h-5 text-slate-500" />
            <p className="text-sm text-slate-500">Progreso Global</p>
          </div>
          <p className="text-2xl font-bold text-slate-900 mb-2">{progresoGlobal.toFixed(1)}%</p>
          <ProgresoBar recaudado={totalRecaudado} objetivo={totalObjetivo} heightClass="h-2" />
        </div>
      </div>

      <AdminFiltersPanel
        filters={filters}
        searchLabel="Buscar campaña"
        searchPlaceholder="Nombre de la campaña..."
        estadoOptions={ESTADO_OPTIONS}
      />

      <EnviosPendientesPanel onRefresh={loadData} />
      <FormErrorAlert error={errorCarga} />
      {errorCarga && <button onClick={loadData}>Reintentar consulta de campañas</button>}

      {(filters.isGlobal || isNacional || isDiocesano) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <CampanasMasterPanel
            key={revisionMaestras}
            nivel={(() => {
              if (filters.isGlobal) return 'global';
              if (isDiocesano) return 'diocesano';
              return 'nacional';
            })()}
            provinciaEclesiasticaId={zonalAccess.provinciaEclesiasticaId || undefined}
            diocesisId={zonalAccess.diocesisId || undefined}
          />
        </div>
      )}

      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4">Desglose por Sede</h2>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          )}
          {!loading && stats.provincias.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Megaphone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No se encontraron campañas</p>
              {hasActiveFilters && <p className="text-sm mt-1">Intenta cambiar los filtros</p>}
            </div>
          )}
          {!loading && stats.provincias.length > 0 && (
            <div className="p-4">
              {renderDesglose()}
            </div>
          )}
        </div>
      </div>

      <CampanaMasterWizardModal
        isOpen={showWizard && puedeCrearMaestra}
        onClose={() => setShowWizard(false)}
        onSuccess={() => { setShowWizard(false); setRevisionMaestras(valor => valor + 1); loadData(); }}
      />

      {/* Sección de Aportes por Campaña - solo para admin diocesano, nacional y global */}
      {(isDiocesano || isNacional || filters.isGlobal) && (
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Aportes por Campaña</h2>
          <FormErrorAlert error={errorAportes} />
          
          {/* Selector de provincia para admin_global */}
          {filters.isGlobal && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-4">
              <Select
                label="Provincia Eclesiástica"
                options={provinciasDisponibles.map((p) => ({ value: p.id, label: p.nombre }))}
                value={provinciaSeleccionadaAportes}
                onChange={(v) => setProvinciaSeleccionadaAportes(v)}
                placeholder={provinciasDisponibles.length === 0 ? 'Cargando...' : 'Seleccionar...'}
                disabled={provinciasDisponibles.length === 0}
              />
            </div>
          )}
          
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loadingAportes && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}
            {!loadingAportes && filters.isGlobal && !provinciaSeleccionadaAportes && (
              <div className="text-center py-12 px-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 inline-block">
                  <Megaphone className="w-10 h-10 mx-auto mb-2 text-blue-400" />
                  <p className="font-medium text-blue-700">Selecciona una provincia eclesiástica</p>
                  <p className="text-sm text-blue-600 mt-1">Elige una provincia para ver las campañas con aportes</p>
                </div>
              </div>
            )}
            {!loadingAportes && !errorAportes && (!filters.isGlobal || provinciaSeleccionadaAportes) && campanasConAportes.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <Megaphone className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="font-medium">No hay campañas master activas</p>
                {filters.isGlobal && (
                  <p className="text-sm mt-1">No hay campañas disponibles para la provincia seleccionada</p>
                )}
              </div>
            )}
            {!loadingAportes && campanasConAportes.length > 0 && (
              <div className="divide-y divide-gray-100">
                {campanasConAportes.map((campana) => {
                  const isExpanded = expandedCampanasMaster.has(campana.campana_master_id);
                  const totalObjetivoCampana = campana.entidades.reduce((s, e) => s + e.monto_objetivo, 0);
                  const totalRecaudadoCampana = campana.entidades.reduce((s, e) => s + e.monto_recaudado, 0);

                  return (
                    <div key={campana.campana_master_id}>
                      <button
                        onClick={() => toggleCampanaMaster(campana.campana_master_id)}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
                            : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                          }
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Megaphone className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="text-left min-w-0">
                            <p className="font-bold text-slate-800 truncate">{campana.campana_master_nombre}</p>
                            <div className="flex items-center gap-3 mt-0.5">
                              <StatusBadge estado={campana.estado} tipo="actividad" />
                              <span className="text-xs text-slate-400">{campana.entidades.length} {isDiocesano ? 'lumisiales' : 'diócesis'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-xs text-slate-400">Objetivo</p>
                            <p className="text-sm font-semibold text-blue-700">{formatCurrency(totalObjetivoCampana)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400">Recaudado</p>
                            <p className="text-sm font-semibold text-emerald-700">{formatCurrency(totalRecaudadoCampana)}</p>
                          </div>
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="border-t border-slate-100 bg-slate-50/30">
                          {/* Encabezado de tabla */}
                          <div className="grid grid-cols-12 gap-2 px-5 py-2 text-xs text-slate-400 border-b border-slate-100">
                            <div className="col-span-6">{isDiocesano ? 'Lumisial' : 'Diócesis'}</div>
                            <div className="col-span-3 text-right">Objetivo</div>
                            <div className="col-span-3 text-right">Recaudado</div>
                          </div>

                          {campana.entidades.map((entidad) => {
                            return (
                              <div
                                key={entidad.entidad_id}
                                className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-b border-slate-100 last:border-b-0 hover:bg-white transition-colors"
                              >
                                <div className="col-span-6 flex items-center gap-2 min-w-0">
                                  <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                  <span className="text-sm font-medium text-slate-700 truncate">{entidad.entidad_nombre}</span>
                                </div>
                                <div className="col-span-3 text-right">
                                  <span className="text-sm text-blue-600">{formatCurrency(entidad.monto_objetivo)}</span>
                                </div>
                                <div className="col-span-3 text-right">
                                  <span className="text-sm text-emerald-600">{formatCurrency(entidad.monto_recaudado)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <AdminDetalleCampanaModal
        isOpen={!!selectedCampana}
        campanaId={selectedCampana?.campanaId ?? ''}
        lumisialNombre={selectedCampana?.lumisialNombre ?? ''}
        lumisialCiudad={selectedCampana?.lumisialCiudad ?? ''}
        diocesisNombre={selectedCampana?.diocesisNombre ?? ''}
        provinciaNombre={selectedCampana?.provinciaNombre ?? ''}
        onClose={() => setSelectedCampana(null)}
      />
    </div>
  );
}
